"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { AstroMascot } from "./AstroMascot";
import { useAutosaveContext, OnboardingProgress } from "./AutosaveManager";
import { 
  Rocket, 
  RotateCcw, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Calendar,
  User,
  Sparkles,
  Star
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface ResumeOnboardingProps {
  onContinue: (progress: OnboardingProgress) => void;
  onStartOver: () => void;
  onShowTerms?: () => void;
}

export function ResumeOnboarding({
  onContinue,
  onStartOver,
  onShowTerms
}: ResumeOnboardingProps) {
  const { loadProgress, clearProgress } = useAutosaveContext();
  const [progress, setProgress] = React.useState<OnboardingProgress | null>(null);
  const [showConfirmStartOver, setShowConfirmStartOver] = React.useState(false);

  React.useEffect(() => {
    const savedProgress = loadProgress();
    setProgress(savedProgress);
  }, [loadProgress]);

  const handleContinue = () => {
    if (progress) {
      onContinue(progress);
    }
  };

  const handleStartOver = () => {
    clearProgress();
    onStartOver();
  };

  const getStepName = (step: string) => {
    const stepMap: Record<string, string> = {
      'astro-otp': 'OTP Verification',
      'astro-bulk': 'Document Upload',
      'astro-review': 'AI Review',
      'astro-joiner-type': 'Joiner Type Selection',
      'astro-adaptive': 'Adaptive Path',
      'astro-final': 'Final Submission',
      'welcome': 'Welcome Dashboard'
    };
    return stepMap[step] || step;
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const saved = new Date(dateString);
    const diffMs = now.getTime() - saved.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (!progress) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/30 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.6, 0.3, 0.6]
          }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        
        {/* Floating Stars */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2"
            style={{
              left: `${20 + (i * 10)}%`,
              top: `${10 + (i * 8)}%`
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 1, 0.3],
              scale: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: i * 0.2
            }}
          >
            <Star className="w-full h-full text-blue-400" />
          </motion.div>
        ))}
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/70 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
            <div className="h-6 w-px bg-gray-300" />
            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
              <Rocket className="w-3 h-3 mr-1" />
              Resume Onboarding
            </Badge>
          </div>
          
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span>Session saved {getTimeAgo(progress.lastSaved)}</span>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 w-full max-w-2xl mx-auto"
        >
          {/* Welcome Back Message */}
          <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 p-8 shadow-xl mb-8 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-4 right-4">
                <Sparkles className="w-12 h-12 text-blue-500" />
              </div>
              <div className="absolute bottom-4 left-4">
                <Rocket className="w-10 h-10 text-purple-500" />
              </div>
            </div>

            <div className="relative z-10">
              <div className="text-center mb-8">
                <motion.div
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="inline-block mb-6"
                >
                  <AstroMascot expression="excited" size="xl" />
                </motion.div>

                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-900 mb-4"
                >
                  Welcome back, space explorer! 🚀
                </motion.h1>

                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-gray-600 mb-6 leading-relaxed"
                >
                  Ready to continue your DayOne journey? I've kept your progress safe while you were away. 
                  Let's pick up exactly where you left off and get you to the finish line!
                </motion.p>
              </div>

              {/* Progress Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6 mb-8"
              >
                <h3 className="text-gray-900 mb-4 flex items-center space-x-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <span>Your Progress Summary</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/80 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Rocket className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Current Step</p>
                        <p className="text-gray-900">{getStepName(progress.currentStep)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/80 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Progress</p>
                        <p className="text-gray-900">
                          {progress.stepIndex} of {progress.totalSteps} steps
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Completion Status</span>
                    <span className="text-sm text-gray-800">
                      {Math.round((progress.stepIndex / progress.totalSteps) * 100)}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <motion.div 
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full relative overflow-hidden"
                      initial={{ width: 0 }}
                      animate={{ width: `${(progress.stepIndex / progress.totalSteps) * 100}%` }}
                      transition={{ duration: 1.5, ease: "easeOut", delay: 0.8 }}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent"
                        animate={{ x: ["-100%", "200%"] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      />
                    </motion.div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Last saved: {getTimeAgo(progress.lastSaved)}</span>
                  </div>
                  {progress.joinerType && (
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span className="capitalize">{progress.joinerType}</span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="space-y-4"
              >
                <Button
                  onClick={handleContinue}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-4 relative overflow-hidden group"
                >
                  <Rocket className="w-5 h-5 mr-3 group-hover:translate-x-1 transition-transform" />
                  Continue from where I left off
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                  
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                    animate={{ x: ["-100%", "100%"] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  />
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setShowConfirmStartOver(true)}
                  className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-gray-300"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Start over instead
                </Button>
              </motion.div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmStartOver && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowConfirmStartOver(false)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md mx-4"
            >
              <Card className="bg-white p-6 shadow-2xl border border-gray-200">
                <h3 className="text-gray-900 mb-2">Start Over?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  This will permanently delete your saved progress. Are you sure you want to start the onboarding process from the beginning?
                </p>
                
                <div className="flex space-x-3">
                  <Button
                    onClick={handleStartOver}
                    variant="destructive"
                    className="flex-1"
                  >
                    Yes, Start Over
                  </Button>
                  <Button
                    onClick={() => setShowConfirmStartOver(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-blue-600 hover:text-blue-700 hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}